import {
  createClient,
  RealtimeChannel,
  SupabaseClient,
} from '@supabase/supabase-js'

type OnMessage = (payload: Record<string, unknown>) => void

type SubscribeArgs = {
  supabaseToken: string
  authId: string
  onMessage: OnMessage
}

export const NOTIFICATIONS_CHANNEL = 'notifications'
export const CALL_CHANNEL = 'CallChannel'

let supabase: SupabaseClient | null = null

/**
 * Teardown in flight, if any.
 *
 * `unsubscribeAll()` drops the client reference synchronously but the actual
 * teardown is async: `removeAllChannels()` waits for each channel's leave push
 * to be acked before the socket closes. Subscribers await this first so a
 * re-subscribe that lands during a teardown cannot open a second websocket
 * while the first one is still leaving.
 */
let pendingTeardown: Promise<void> | null = null

/**
 * Channels currently open on the shared client, keyed by topic.
 *
 * `realtime-js` v2 does NOT de-duplicate topics: `client.channel('x')` always
 * builds a brand new channel and pushes it onto `realtime.channels`. Without
 * this map, every call to the subscribe helpers below opens another channel on
 * the same socket, and we quickly cross Supabase's per-connection channel cap
 * (100) and the project-wide joins/sec quota. Keeping one channel per topic
 * makes the helpers idempotent.
 */
const channelsByTopic = new Map<string, RealtimeChannel>()

const getClient = ({
  authId,
  supabaseToken,
}: Pick<SubscribeArgs, 'authId' | 'supabaseToken'>): SupabaseClient | null => {
  if (!authId || !supabaseToken) {
    // Subscribing is single-shot per mount, so this is terminal for the
    // session rather than something the next render retries. Log it or the
    // symptom (no live notifications, no call-state updates) is undebuggable.
    console.warn(
      'Supabase realtime not started: missing Auth0 credentials (supabaseAccessToken / sub).'
    )
    return null
  }

  if (!supabase) {
    supabase = createClient(
      process.env.SUPABASE_URL,
      process.env.SUPABASE_ANON_KEY,
      {
        global: {
          headers: {
            Authorization: `Bearer ${supabaseToken}`,
          },
        },
      }
    )
  }

  // Always (re)apply the token: it is refreshed by Auth0 over the life of a
  // tab, and Realtime rejects joins once the JWT it holds has expired.
  supabase.realtime.setAuth(supabaseToken)

  return supabase
}

/**
 * Re-applies a freshly minted JWT to the live socket.
 *
 * Subscribing happens once per mount, so without this the socket would keep
 * using the token captured at mount time. Once that token expires, any rejoin
 * after a network blip is rejected and the channel retries forever. Callers
 * should invoke this periodically for as long as they stay subscribed.
 */
export const applyRealtimeToken = (supabaseToken: string): void => {
  if (!supabase || !supabaseToken) {
    return
  }

  supabase.realtime.setAuth(supabaseToken)
}

/**
 * Opens `topic` if it is not open yet, otherwise returns the live channel.
 *
 * `configure` only runs for a channel that is actually being created, so
 * repeat calls never register duplicate `postgres_changes` bindings. The
 * corollary is that an `onMessage` passed for an already-open topic is
 * DISCARDED — the caller keeps receiving messages on the handler bound when
 * the channel was first opened. Today `WebSocketsProvider` is the only caller
 * and its cleanup always clears the map, so the two stay in sync; a second
 * caller would need `unsubscribeAll()` (or a per-caller channel) to rebind.
 */
const getOrCreateChannel = ({
  client,
  topic,
  configure,
}: {
  client: SupabaseClient
  topic: string
  configure: (channel: RealtimeChannel) => RealtimeChannel
}): RealtimeChannel => {
  const existing = channelsByTopic.get(topic)
  if (existing) {
    return existing
  }

  const channel = configure(client.channel(topic))
  channelsByTopic.set(topic, channel)

  channel.subscribe((status) => {
    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
      console.warn(`Supabase realtime channel "${topic}" status: ${status}`)
    }
    if (status === 'CLOSED') {
      // The channel is gone (server close, quota rejection, or our own
      // teardown). Drop it so a later subscribe can open a fresh one instead
      // of handing back a dead channel.
      if (channelsByTopic.get(topic) === channel) {
        channelsByTopic.delete(topic)
      }
    }
  })

  return channel
}

/**
 * Subscribes to notification INSERTs. Idempotent per topic: if the channel is
 * already open the live one is returned and `onMessage` is ignored (see
 * `getOrCreateChannel`). Returns `null` when credentials are missing.
 */
export const subscribeToNotifications = async ({
  supabaseToken,
  authId,
  onMessage,
}: SubscribeArgs): Promise<RealtimeChannel | null> => {
  await pendingTeardown

  const client = getClient({ authId, supabaseToken })
  if (!client) {
    return null
  }

  return getOrCreateChannel({
    client,
    topic: NOTIFICATIONS_CHANNEL,
    configure: (channel) =>
      channel.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
        },
        onMessage
      ),
  })
}

/**
 * Subscribes to CallChannel INSERTs and UPDATEs. Idempotent per topic: if the
 * channel is already open the live one is returned and `onMessage` is ignored
 * (see `getOrCreateChannel`). Returns `null` when credentials are missing.
 */
export const subscribeToCallChannel = async ({
  supabaseToken,
  authId,
  onMessage,
}: SubscribeArgs): Promise<RealtimeChannel | null> => {
  await pendingTeardown

  const client = getClient({ authId, supabaseToken })
  if (!client) {
    return null
  }

  return getOrCreateChannel({
    client,
    topic: CALL_CHANNEL,
    configure: (channel) =>
      channel
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'CallChannel',
          },
          onMessage
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'CallChannel',
          },
          onMessage
        ),
  })
}

const teardownClient = async (client: SupabaseClient): Promise<void> => {
  try {
    await client.removeAllChannels()
  } catch (error) {
    console.warn('Failed to tear down Supabase realtime channels', error)
    // Nothing references this client any more, so there is no retry after
    // this point: close the socket directly or it stays open (and counted
    // against the concurrent-connection quota) for the life of the tab.
    try {
      client.realtime.disconnect()
    } catch (disconnectError) {
      console.warn('Failed to close Supabase realtime socket', disconnectError)
    }
  }

  // `createClient` builds a GoTrueClient with `autoRefreshToken: true`, which
  // registers a `visibilitychange` listener and a refresh interval. Dropping
  // the client reference does not unregister either, so without this every
  // teardown/re-subscribe cycle would leak one of each.
  try {
    await client.auth.stopAutoRefresh()
  } catch (error) {
    console.warn('Failed to stop Supabase auth auto-refresh', error)
  }
}

/**
 * Unsubscribes every open channel and closes the underlying websocket.
 *
 * `removeAllChannels()` disconnects the socket once the last channel is gone,
 * which is what frees the Supabase "concurrent peak connection" slot. The
 * client reference is dropped too, so the next subscribe builds a client with a
 * current `Authorization` header rather than reusing the one baked in at first
 * login. Call this whenever the subscriber unmounts or the user signs out.
 */
export const unsubscribeAll = async (): Promise<void> => {
  channelsByTopic.clear()

  const client = supabase
  supabase = null

  if (!client) {
    return
  }

  const teardown = teardownClient(client)
  pendingTeardown = teardown

  try {
    await teardown
  } finally {
    if (pendingTeardown === teardown) {
      pendingTeardown = null
    }
  }
}
